/**
 * "Is the client a Healthcare worker?" has two places to be answered and only one
 * meaning, so the form answers it once and derives the other.
 *
 * The Yes/No radio (`healthcareWorker`) and the Indication `X0069-17` "Is the client a
 * Healthcare Worker (HCW)" are put to the clinician over exactly the same window —
 * Influenza, aged 16 to 69 at the vaccination date — the radio by
 * `isHealthcareWorkerQuestionAsked`, the code by `RiskFactorService.isAskedAtAge`. Asked
 * twice, they can disagree, and the disagreement is not cosmetic: `healthcareWorker` is
 * persisted to `healthcare_worker` and read by two validation rules, and goes no further.
 * It is in no HL7 segment. `X0069-17` is the only form the answer takes on the wire.
 *
 * So a Yes on the radio with the code unticked tells NIIS nothing about the healthcare
 * worker — while simultaneously waiving the "at least one Indication" requirement
 * (`checkMissingRiskFactors` short-circuits on a true `healthcareWorker`), letting the
 * dose be reported with no Indications at all. The mirror case, a ticked code with a No,
 * is a record that contradicts itself.
 *
 * The radio is therefore the single answer, `X0069-17` is withdrawn from the Indications
 * the clinician can tick, and this derives the code from the radio.
 */
export const HEALTHCARE_WORKER_RISK_CODE = "X0069-17";

/**
 * The risk-factor codes a form should hold once the healthcare-worker answer is applied.
 *
 * Withdraws as well as adds, for the reason `mergeDefaultedRiskCodes` does (KIN-1870): an
 * answer corrected from Yes to No that leaves the code behind asserts an eligibility the
 * clinician has just denied. So does a date moved out of 16-69, which takes the question
 * away entirely — the backend stops offering `X0069-17` there, and a leftover tick would
 * be the only thing still claiming it. Codes the clinician ticked themselves are never
 * touched, and a selection that already agrees with the answer is returned as it stands.
 *
 * An existing record is returned unchanged: its stored codes are what was reported to
 * NIIS, and re-deriving them on open would edit history rather than the form.
 */
export const applyHealthcareWorkerRiskCode = ({
  currentCodes = [],
  isQuestionAsked = false,
  healthcareWorker = null,
  isEditMode = false,
}) => {
  const hasCode = currentCodes.includes(HEALTHCARE_WORKER_RISK_CODE);
  const isHealthcareWorker = isQuestionAsked && healthcareWorker === true;

  if (isEditMode || hasCode === isHealthcareWorker) return currentCodes;

  return isHealthcareWorker
    ? [...currentCodes, HEALTHCARE_WORKER_RISK_CODE]
    : currentCodes.filter((code) => code !== HEALTHCARE_WORKER_RISK_CODE);
};

/**
 * The Indications to offer the clinician, with `X0069-17` removed while the radio above
 * the list is asking the same thing. Offering both is the defect; the list is the one
 * that goes, because the radio is what the backend requires an answer to.
 */
export const withoutHealthcareWorkerOption = ({
  riskFactors,
  isQuestionAsked = false,
  isEditMode = false,
}) => {
  if (!riskFactors || isEditMode || !isQuestionAsked) return riskFactors;

  const selectableRisks = (riskFactors.selectableRisks || []).filter(
    (risk) => risk.code !== HEALTHCARE_WORKER_RISK_CODE
  );

  if (selectableRisks.length === (riskFactors.selectableRisks || []).length) {
    return riskFactors;
  }

  return { ...riskFactors, selectableRisks };
};
